import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { Download, Upload, Expand, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";

const DEFAULT_ACCEPT = ".pdf,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.webp,.gif";

const FileUploadCard = ({
  label = "Reference file (optional)",
  value,
  onChange,
  accept = DEFAULT_ACCEPT,
  baseUrl = "",
  compact = false,
  className,
  labelClassName,
  frameClassName,
}) => {
  const inputRef = useRef(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const objectUrl = useMemo(() => {
    if (value?.file instanceof File) {
      return URL.createObjectURL(value.file);
    }
    return null;
  }, [value]);

  useEffect(
    () => () => objectUrl && URL.revokeObjectURL(objectUrl),
    [objectUrl],
  );

  const displayName =
    (value?.file && value.file.name) ||
    value?.name ||
    (value?.url ? value.url.split("/").pop() : null);

  const effectiveUrl = useMemo(() => {
    if (objectUrl) return objectUrl;
    if (value?.url && value.url !== "undefined") {
      try {
        new URL(value.url);
        return value.url;
      } catch {
        return baseUrl
          ? `${baseUrl.replace(/\/$/, "")}/${value.url.replace(/^\//, "")}`
          : value.url;
      }
    }
    return null;
  }, [objectUrl, value, baseUrl]);

  const sizeText = useMemo(() => {
    const bytes = value?.file?.size ?? value?.size;
    if (!bytes && bytes !== 0) return null;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024)
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
  }, [value]);

  const handleFile = (file) => {
    if (!file) return;
    onChange?.({ file, uploadedAt: new Date() });
  };

  const onBrowseClick = () => inputRef.current?.click();

  const isImage = useMemo(() => {
    const mime = value?.file?.type || "";
    if (mime.startsWith("image/")) return true;
    const name = (displayName || "").toLowerCase();
    return /(\.png|\.jpe?g|\.webp|\.gif|\.bmp|\.svg)$/.test(name);
  }, [value, displayName]);

  const openPreview = useCallback(() => {
    if (!effectiveUrl) return;
    if (isImage) {
      setIsPreviewOpen(true);
    } else {
      window.open(effectiveUrl, "_blank", "noopener,noreferrer");
    }
  }, [effectiveUrl, isImage]);

  const downloadCurrent = useCallback(async () => {
    if (!effectiveUrl) return;

    if (value?.file instanceof File) {
      const localUrl = URL.createObjectURL(value.file);
      const anchor = document.createElement("a");
      anchor.href = localUrl;
      anchor.download = displayName || "download";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(localUrl);
      return;
    }

    try {
      const resolved = new URL(effectiveUrl, window.location.href);
      const fetchOptions = {
        credentials:
          resolved.origin === window.location.origin ? "include" : "omit",
      };

      const resp = await fetch(resolved.toString(), fetchOptions);
      if (!resp.ok) throw new Error(`Failed to fetch file (${resp.status})`);
      const blob = await resp.blob();

      const cd = resp.headers.get("content-disposition");
      let filename = displayName || "download";
      const match = cd && cd.match(/filename\*?=(?:UTF-8'')?("?)([^";]+)\1/i);
      if (match && match[2]) {
        try {
          filename = decodeURIComponent(match[2]);
        } catch {
          filename = match[2];
        }
      }

      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(error);
    }
  }, [effectiveUrl, value?.file, displayName]);

  const hasAnyFile = !!(
    value?.file ||
    (value?.url && value.url !== "undefined")
  );

  useEffect(() => {
    if (!isPreviewOpen) return;
    const onKey = (event) => {
      if (event.key === "Escape") setIsPreviewOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isPreviewOpen]);

  return (
    <div className={cn("space-y-2", className)}>
      <Label className={cn(compact ? "text-sm" : "text-xs", labelClassName)}>
        {label}
      </Label>

      <div
        className={cn(
          "rounded-radius border border-border bg-card shadow-sm",
          compact && "h-9 px-2",
          frameClassName,
        )}
      >
        <div
          className={cn(
            "flex items-center gap-3 h-full",
            compact ? "px-1.5" : "px-2 py-1",
          )}
        >
          <div className="min-w-0 flex-1">
            {hasAnyFile ? (
              <>
                <div
                  className={cn(
                    "font-medium text-foreground truncate",
                    compact ? "text-sm" : "text-xs",
                  )}
                >
                  {displayName || "File"}
                </div>
                {(sizeText || value?.uploadedAt) && (
                  <div className="text-[10px] text-muted-foreground" />
                )}
              </>
            ) : (
              <div className="text-xs font-medium text-muted-foreground truncate">
                Type: pdf, csv, xlsx, png, jpg
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {hasAnyFile && (
              <>
                <button
                  type="button"
                  className={cn(
                    "rounded-radius text-xs flex items-center text-foreground",
                    compact ? "h-9 px-3" : "px-2 py-1",
                  )}
                  onClick={openPreview}
                  title="Preview"
                >
                  <Expand className="mr-1 h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className={cn(
                    "rounded-radius text-xs flex items-center text-foreground",
                    compact ? "h-9 px-3" : "px-2 py-1",
                  )}
                  onClick={downloadCurrent}
                  title="Download"
                >
                  <Download className="mr-1 h-3.5 w-3.5" />
                </button>
              </>
            )}
            <button
              type="button"
              className={cn(
                "rounded-radius text-xs flex items-center text-foreground",
                compact ? "h-9 px-3" : "px-2 py-1",
              )}
              onClick={onBrowseClick}
              title={hasAnyFile ? "Replace file" : "Upload"}
            >
              <Upload className="mr-1 h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        onChange={(event) => {
          const file =
            event.target.files && event.target.files[0]
              ? event.target.files[0]
              : null;
          if (file) handleFile(file);
          if (inputRef.current) inputRef.current.value = "";
        }}
      />

      {isPreviewOpen && isImage && effectiveUrl && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setIsPreviewOpen(false)}
        >
          <div
            className="relative max-w-[90vw] max-h-[85vh]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close preview"
              className="absolute -top-3 -right-3 rounded-full bg-white shadow p-1"
              onClick={() => setIsPreviewOpen(false)}
            >
              <X className="h-4 w-4" />
            </button>
            <img
              src={effectiveUrl}
              alt={displayName || "Preview"}
              crossOrigin="anonymous"
              className="rounded-radius shadow max-h-[85vh] max-w-[90vw] object-contain"
              draggable={false}
            />
            <div className="mt-2 text-center text-xs text-white/90">
              {displayName}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadCard;
